import { useCallback, useEffect, useState } from 'react';
import { SubscriptionPlanResponse } from 'shared/types/Subscription';

import { useUserContext } from '@/context/UserContext';
import Sentry from '@/services/sentry';
import { SubscriptionsAPI } from '@/services/subscriptions';

const getWebSubscriptionPlans = (plans: SubscriptionPlanResponse[]) => {
    const premium =
        plans.find(
            (p) =>
                p.platform === 'ios' &&
                p.billing_period === 'monthly' &&
                p.seats === 1,
        ) || null;
    const teams =
        plans.find(
            (p) =>
                p.platform === 'ios' &&
                p.billing_period === 'monthly' &&
                p.seats === 2,
        ) || null;

    return { premium, teams };
};

export const useSubscriptionsPlans = () => {
    const { userProfileData } = useUserContext();

    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [allPlans, setAllPlans] = useState<SubscriptionPlanResponse[]>([]);
    const [webPlans, setWebPlans] = useState<{
        premium: SubscriptionPlanResponse | null;
        teams: SubscriptionPlanResponse | null;
    }>({ premium: null, teams: null });

    const fetchSubscriptionPlans = useCallback(async () => {
        try {
            const plans = await SubscriptionsAPI.fetchSubscriptionsPlans();
            setAllPlans(plans);
            setWebPlans(getWebSubscriptionPlans(plans));
        } catch (err) {
            setError(err.message || 'Failed to fetch subscriptions');
            Sentry.recordError(err);
        } finally {
            setIsLoading(false);
        }
    }, [setIsLoading, setError, setAllPlans]);

    useEffect(() => {
        if (userProfileData) {
            fetchSubscriptionPlans();
        }
    }, [userProfileData, fetchSubscriptionPlans]);

    return {
        isLoading,
        error,
        allPlans,
        webPlans,
    };
};
